import { checkoutUrl, CheckoutUrlDialog, CheckoutUrlQueryParams } from "@dashboard/checkouts/urls";
import ActionDialog from "@dashboard/components/ActionDialog";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import {
  TransactionActionEnum,
  usePaymentRecoveryCreateOrderMutation,
  usePaymentRecoveryDetailsQuery,
  usePaymentRecoveryTransactionActionMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { sectionNames } from "@dashboard/intl";
import { getMutationStatus } from "@dashboard/misc";
import { orderUrl } from "@dashboard/orders/urls";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import PaymentRecoveryDetailsPage from "../../components/PaymentRecoveryDetailsPage";
import { paymentRecoveryMessages } from "../../messages";
import { getGatewayName, getHeldMoney, getUnresolvedRefundAttempts } from "../../utils";

export interface PaymentRecoveryDetailsViewProps {
  id: string;
  params: CheckoutUrlQueryParams;
}

export const PaymentRecoveryDetailsView = ({ id, params }: PaymentRecoveryDetailsViewProps) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const { data, loading, refetch } = usePaymentRecoveryDetailsQuery({
    displayLoader: true,
    variables: { id },
    // `channel.paymentSettings` needs MANAGE_CHANNELS or HANDLE_PAYMENTS. Staff
    // holding only MANAGE_CHECKOUTS get a field-level error for it; keep the
    // rest of the checkout rendering and just drop the auto-refund countdown.
    errorPolicy: "all",
  });
  const checkout = data?.checkout ?? undefined;

  const [openModal, closeModal] = createDialogActionHandlers<
    CheckoutUrlDialog,
    CheckoutUrlQueryParams
  >(navigate, urlParams => checkoutUrl(id, urlParams), params);

  const [createOrder, createOrderOpts] = usePaymentRecoveryCreateOrderMutation({
    onCompleted: result => {
      const errors = result.orderCreateFromCheckout?.errors ?? [];

      if (errors.length > 0) {
        notify({ status: "error", text: errors[0].message ?? "" });

        return;
      }

      const order = result.orderCreateFromCheckout?.order;

      if (order) {
        notify({
          status: "success",
          text: intl.formatMessage(paymentRecoveryMessages.orderCreated, {
            number: order.number,
          }),
        });
        closeModal();
        navigate(orderUrl(order.id));
      }
    },
  });

  const [requestAction, requestActionOpts] = usePaymentRecoveryTransactionActionMutation({
    onCompleted: result => {
      const errors = result.transactionRequestAction?.errors ?? [];

      if (errors.length > 0) {
        notify({ status: "error", text: errors[0].message ?? "" });

        return;
      }

      notify({
        status: "success",
        text: intl.formatMessage(paymentRecoveryMessages.actionRequested),
      });
      closeModal();
      refetch();
    },
  });

  const held = getHeldMoney(checkout?.transactions ?? null);
  const isCaptured = held.charged.amount > 0;
  const unresolvedRefundAttempts = getUnresolvedRefundAttempts(checkout?.transactions ?? null);

  const handleReturnMoney = async () => {
    // Money can be spread over several attempts. Captured funds are refunded,
    // reserved-only funds are voided — asking for the wrong one is rejected by
    // the payment app, so each transaction is matched to its own remedy.
    const transactions = checkout?.transactions ?? [];

    for (const transaction of transactions) {
      if (transaction.chargedAmount.amount > 0) {
        await requestAction({
          variables: { id: transaction.id, actionType: TransactionActionEnum.REFUND },
        });
      } else if (transaction.authorizedAmount.amount > 0) {
        await requestAction({
          variables: { id: transaction.id, actionType: TransactionActionEnum.CANCEL },
        });
      }
    }
  };

  // A checkout disappears the moment it becomes an order, so a miss here
  // usually means someone else already resolved it.
  React.useEffect(() => {
    if (!loading && !checkout) {
      notify({
        status: "error",
        text: intl.formatMessage(paymentRecoveryMessages.loadError),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, checkout]);

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.paymentRecovery)} />
      <PaymentRecoveryDetailsPage
        checkout={checkout}
        loading={loading}
        onCreateOrder={() => openModal("create-order")}
        onRefund={() => openModal("refund")}
      />
      <ActionDialog
        open={params.action === "create-order"}
        title={intl.formatMessage(paymentRecoveryMessages.createOrderDialogTitle)}
        confirmButtonLabel={intl.formatMessage(paymentRecoveryMessages.createOrder)}
        confirmButtonState={getMutationStatus(createOrderOpts)}
        onClose={closeModal}
        onConfirm={() => createOrder({ variables: { id } })}
      >
        {intl.formatMessage(paymentRecoveryMessages.createOrderDialogContent, {
          token: checkout?.email ?? id,
          amount: `${held.charged.currency} ${(isCaptured ? held.charged : held.authorized).amount.toFixed(2)}`,
        })}
      </ActionDialog>
      <ActionDialog
        open={params.action === "refund"}
        variant="delete"
        title={intl.formatMessage(paymentRecoveryMessages.refundDialogTitle)}
        confirmButtonLabel={intl.formatMessage(
          isCaptured ? paymentRecoveryMessages.refund : paymentRecoveryMessages.voidAction,
        )}
        confirmButtonState={getMutationStatus(requestActionOpts)}
        onClose={closeModal}
        onConfirm={handleReturnMoney}
      >
        <Text as="p">
          {intl.formatMessage(paymentRecoveryMessages.refundDialogContent, {
            token: checkout?.email ?? id,
            amount: `${held.charged.currency} ${(isCaptured ? held.charged : held.authorized).amount.toFixed(2)}`,
          })}
        </Text>
        {unresolvedRefundAttempts.length > 0 && (
          <Box
            marginTop={4}
            padding={4}
            borderRadius={3}
            borderWidth={1}
            borderStyle="solid"
            borderColor="warning1"
            backgroundColor="warning1"
          >
            <Text as="p" fontWeight="bold" marginBottom={2}>
              {intl.formatMessage(paymentRecoveryMessages.refundWarningTitle)}
            </Text>
            {unresolvedRefundAttempts.map(({ transaction, status, lastEvent }) => (
              <Text as="p" key={transaction.id} size={2}>
                {intl.formatMessage(
                  status === "pending"
                    ? paymentRecoveryMessages.refundWarningPending
                    : paymentRecoveryMessages.refundWarningFailed,
                  {
                    gateway: getGatewayName(transaction) ?? "-",
                    amount: `${transaction.chargedAmount.currency} ${transaction.chargedAmount.amount.toFixed(2)}`,
                    date: new Date(lastEvent.createdAt).toLocaleString(),
                    message: lastEvent.message || "-",
                  },
                )}
              </Text>
            ))}
          </Box>
        )}
      </ActionDialog>
    </>
  );
};

PaymentRecoveryDetailsView.displayName = "PaymentRecoveryDetailsView";
export default PaymentRecoveryDetailsView;
